import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { tableConfig, uiConfig, getDateFormat } from '../../config/appConfig';
import CustomRiskAssessmentSplitView from './CustomRiskAssessmentSplitView';
import RiskAssessmentActionIntegration from './components/RiskAssessmentActionIntegration';
import RiskAssessmentReferenceDocument from './components/RiskAssessmentReferenceDocument';

const riskAssessmentTrackingLabels = getLabels('RiskAssessment');
const riskAssessmentTrackingBase = createTrackingConfig('RiskAssessment');

export const riskAssessmentConfig = {
  entityName: 'RiskAssessment',
  
  // Entity information for file attachments
  entityTypeId: 4, // Risk Assessment entity type ID (confirmed from backend)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/risk-assessment',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      duplicate: (id) => `/${id}/duplicate`,
      bulkDelete: '/bulk-delete',
      // File attachment endpoints
      fileAttachments: (id) => `/${id}/file-attachments`,
      fileAttachmentCount: (id) => `/${id}/file-attachment-count`,
      withFileAttachments: (id) => `/${id}/with-file-attachments`
    },
    lookupEndpoints: {
      riskCategories: '/risk-assessment/relations/risk-categories',
      riskAssessmentStatuses: '/risk-assessment-status',
      users: '/risk-assessment/relations/users',
      departments: '/department'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/risk-assessment',
      withFileAttachments: '/risk-assessment/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '50%', // 50% of table width 
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',  // 'default' | 'modern' | 'cards' | 'board' | 'timeline'
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    showDuplicateButton: true, // Show duplicate button in table actions
    // Default visible columns - only show the most important ones initially
    defaultVisibleColumns: [
      'code', 'title', 'riskCategory', 'parameter', 'levelOfRisk', 'result',
      'status', 'requestedDate', 'receivedDate', 'fileAttachmentCount'
    ],
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 4, // Risk Assessment master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
    enableSearch: true,
    enableFilters: true,
    enableExport: true,
    enableImport: true,
    enableAuditLog: true,
    enableComments: true,
    enableAttachments: true,
    enableWorkflow: true,
    enableNotifications: true
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in risk assessment form
    showInDetails: true, // Show review and approval integration in risk assessment details tab
    showInActions: false, // Show review and approval integration in risk mitigation actions tab
    requireApprovalForClose: false, // Require review and approval before closing risk assessment
    allowMultipleApprovals: true, // Allow multiple review and approval requests per risk assessment
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request Risk Assessment Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'ExclamationTriangleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in risk assessment view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in risk assessment timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },

  // Custom Split View Panel configuration for Risk Assessment module
  customSplitViewPanel: {
    enabled: true,
    component: CustomRiskAssessmentSplitView  // Use the custom Risk Assessment split view component
  },
  
  // Display Configuration
  displayName: 'Risk Assessment Management',
  description: 'Risk assessment and management system for identifying, analyzing, and mitigating risks',
  icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'riskCategory', 'status', 'responsibleForActionType', 'responsibleForActionUsers', 'responsibleForActionRoles', 'responsibleForActionDepartments', 'involvedDepartments',
    'createdBy', 'updatedBy', 'createdById', 'updatedById', 'createdAt', 'updatedAt', 'riskAssessmentTimeLineEntries',
    'riskAssessmentActions', 'fileAttachments', 'fileAttachmentCount'
  ],

  // Field mappings for display values
  fieldMappings: {
    // Map frontend display fields to backend field paths
    riskCategory: 'riskCategory.name',
    status: 'status.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'Enter risk assessment code',
      table: { header: 'Code', width: '140px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'Risk assessment code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: false,
      placeholder: 'Enter risk assessment title',
      table: { header: 'Title', cellRenderer: 'lineClamp', maxLines: 3, width: '200px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 200,
        message: 'Title must not exceed 200 characters'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      useFormFileUpload2: true, // Use the new responsive FormFileUpload_2 component
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
    {
      name: 'riskCategoryId',
      label: 'Risk Category',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskCategories',
      options: [],
      table: {
        header: 'Category',
        width: '150px',
        sortable: true,
        displayField: 'riskCategory',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid risk category'
      }
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'riskAssessmentStatuses',
      table: {
        header: 'Status',
        width: '120px',
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      }
    },

    // Date fields
    {
      name: 'requestedDate',
      label: 'Requested Date',
      type: 'date',
      required: false,
      placeholder: 'Select requested date',
      table: { 
        header: 'Requested Date', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'date',
      validation: {
        required: false,
        message: 'Please enter a valid date or leave empty'
      }
    },
    {
      name: 'receivedDate',
      label: 'Received Date',
      type: 'date',
      required: false,
      placeholder: 'Select received date',
      table: { 
        header: 'Received Date', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'date',
      validation: {
        required: false,
        message: 'Please enter a valid date or leave empty'
      }
    },
    {
      name: 'closedAt',
      label: 'Closed At',
      type: 'date',
      required: false,
      placeholder: 'Select closure date',
      table: { 
        header: 'Closed At', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'date',
      validation: {
        required: false,
        message: 'Please enter a valid date or leave empty'
      }
    },
    // Risk Event Description
    {
      name: 'parameter',
      label: 'Parameter (Risk Event Description)',
      type: 'textarea',
      required: true,
      placeholder: 'Enter risk event/parameter description (e.g., "Άρνηση παραλαβής λόγω κατεστραμμένων τεμαχίων")',
      rows: 3,
      table: { header: 'Parameter', cellRenderer: 'lineClamp', maxLines: 3, width: '300px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 1000,
        message: 'Parameter is required and must not exceed 1000 characters'
      }
    },
    // Consequence/Effect
    {
      name: 'effect',
      label: 'Effect (Consequence)',
      type: 'textarea',
      required: false,
      placeholder: 'Enter effect/consequence description (e.g., "Επιστροφή των τεμαχίων στην ΑΩ")',
      rows: 3,
      table: { header: 'Effect', cellRenderer: 'lineClamp', maxLines: 3, width: '300px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Effect must not exceed 1000 characters'
      }
    },
    // Impact Assessment
    {
      name: 'impact',
      label: 'Impact',
      type: 'number',
      required: false,
      placeholder: 'Enter impact value (1-10 scale)',
      table: { header: 'Impact', width: '100px', sortable: true },
      filterable: true,
      filterType: 'number',
      validation: {
        integer: true,
        min: 1,
        max: 10,
        message: 'Impact must be a number between 1 and 10'
      }
    },
    // Rationale/Explanation
    {
      name: 'rationale',
      label: 'Rationale (Explanation)',
      type: 'textarea',
      required: false,
      placeholder: 'Enter explanation of the risk and its cause',
      rows: 3,
      table: { header: 'Rationale', cellRenderer: 'lineClamp', maxLines: 3, width: '300px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 4000,
        message: 'Rationale must not exceed 4000 characters'
      }
    },
    // Risk Level
    {
      name: 'levelOfRisk',
      label: 'Level of Risk',
      type: 'number',
      required: false,
      placeholder: 'Enter level of risk (1-10 scale)',
      table: { header: 'Level of Risk', width: '120px', sortable: true },
      filterable: true,
      filterType: 'number',
      validation: {
        integer: true,
        min: 1,
        max: 10,
        message: 'Level of risk must be a number between 1 and 10'
      }
    },
    // Result
    {
      name: 'result',
      label: 'Result',
      type: 'number',
      required: false,
      placeholder: 'Enter result value',
      table: { header: 'Result', width: '100px', sortable: true },
      filterable: true,
      filterType: 'number',
      validation: {
        integer: true,
        message: 'Result must be a valid number'
      }
    },
    // Responsible for Action Type (selection: User, Role, Department)
    {
      name: 'responsibleForActionType',
      label: 'Responsible For Action Type',
      type: 'select',
      required: false,
      options: [
        { value: 'USER', label: 'User' },
        { value: 'ROLE', label: 'Role' },
        { value: 'DEPARTMENT', label: 'Department' }
      ],
      table: {
        header: 'Responsible Type',
        width: '150px',
        sortable: true
      },
      filterable: true,
      filterType: 'select'
    },
    // Responsible for Action User (multiselect)
    {
      name: 'responsibleForActionUserIds',
      label: 'Responsible Users',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      dependsOn: {
        field: 'responsibleForActionType',
        value: 'USER'
      },
      validation: {
        array: true,
        message: 'Please select at least one user'
      },
      table: {
        header: 'Responsible Users',
        width: '200px',
        sortable: false,
        displayField: 'responsibleForActionUsers',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Responsible for Action Role (multiselect)
    {
      name: 'responsibleForActionRoleIds',
      label: 'Responsible Roles',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'roles',
      options: [],
      dependsOn: {
        field: 'responsibleForActionType',
        value: 'ROLE'
      },
      validation: {
        array: true,
        message: 'Please select at least one role'
      },
      table: {
        header: 'Responsible Roles',
        width: '200px',
        sortable: false,
        displayField: 'responsibleForActionRoles',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Responsible for Action Department (multiselect)
    {
      name: 'responsibleForActionDepartmentIds',
      label: 'Responsible Departments',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      dependsOn: {
        field: 'responsibleForActionType',
        value: 'DEPARTMENT'
      },
      validation: {
        array: true,
        message: 'Please select at least one department'
      },
      table: {
        header: 'Responsible Departments',
        width: '200px',
        sortable: false,
        displayField: 'responsibleForActionDepartments',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Involved Departments (multiselect)
    {
      name: 'involvedDepartmentsIds',
      label: 'Involved Departments',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      validation: {
        array: true,
        message: 'Please select at least one department'
      },
      table: {
        header: 'Departments',
        width: '200px',
        sortable: false,
        displayField: 'involvedDepartments',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      table: {
        header: 'Document Reference',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 40,
      },
      filterable: true,
      filterType: 'text',
    },
  ],

  // Table Configuration
  tableConfig: {
    defaultSort: { field: 'code', direction: 'desc' },
    defaultPageSize: 25,
    stickyHeader: true,
    rowSelection: true,
    exportOptions: ['csv', 'excel', 'pdf'],
    // Column management
    enableColumnResize: true,
    enableColumnReorder: true,
    enableColumnVisibility: true,
    // Performance settings
    virtualization: true, // For large datasets
    maxVisibleColumns: 8, // Limit visible columns for better UX
  },

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'riskCategoryId', 'statusId'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8',
        'riskCategoryId': 'md:col-span-6',
        'statusId': 'md:col-span-6'
      }
    },
    {
      title: 'Timeline & Dates',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['requestedDate', 'receivedDate', 'closedAt']
    },
    {
      title: 'Risk Event Description',
      icon: '📝',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['parameter']
    },
    {
      title: 'Consequence & Impact',
      icon: '⚡',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['effect', 'impact', 'rationale'],
      fieldSpans: {
        'effect': 'md:col-span-12',
        'impact': 'md:col-span-4',
        'rationale': 'md:col-span-8'
      }
    },
    {
      title: 'Risk Assessment',
      icon: '📊',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['levelOfRisk', 'result']
    },
    {
      title: 'Responsible & Departments',
      icon: '👥',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['responsibleForActionType', 'responsibleForActionUserIds', 'responsibleForActionRoleIds', 'responsibleForActionDepartmentIds', 'involvedDepartmentsIds'],
      fieldSpans: {
        'responsibleForActionType': 'md:col-span-3',
        'responsibleForActionUserIds': 'md:col-span-9',
        'responsibleForActionRoleIds': 'md:col-span-9',
        'responsibleForActionDepartmentIds': 'md:col-span-9',
        'involvedDepartmentsIds': 'md:col-span-12'
      }
    },
    {
      title: 'Actions',
      icon: '⚡',
      layout: 'custom',
      customComponent: RiskAssessmentActionIntegration
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      customComponent: RiskAssessmentReferenceDocument,
      fields: [],
    }
  ],

  // Filter Groups Configuration - Groups filters into tabs for better organization
  // Based on form sections for consistency
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'code', 'title', 'statusId', 'riskCategoryId'
      ]
    },
    {
      name: 'dates',
      label: 'Dates & Timeline',
      icon: '📅',
      fields: [
        'requestedDate', 'receivedDate', 'closedAt'
      ]
    },
    {
      name: 'assessment',
      label: 'Risk Assessment',
      icon: '📊',
      fields: [
        'parameter', 'effect', 'impact', 'rationale', 'levelOfRisk', 'result'
      ]
    },
    {
      name: 'people',
      label: 'People & Departments',
      icon: '👥',
      fields: [
        'responsibleForActionType', 'responsibleForActionUserIds', 'responsibleForActionRoleIds', 'responsibleForActionDepartmentIds', 'involvedDepartmentsIds'
      ]
    }
  ],

  // Custom Actions
  customActions: [
    {
      name: 'approve',
      label: 'Approve Assessment',
      icon: 'M5 13l4 4L19 7',
      endpoint: '/approve',
      method: 'POST',
      confirmMessage: 'Are you sure you want to approve this risk assessment?',
      successMessage: 'Risk assessment approved successfully',
      requiresSelection: true
    },
    {
      name: 'reassess',
      label: 'Trigger Re-assessment',
      icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      endpoint: '/reassess',
      method: 'POST',
      confirmMessage: 'Are you sure you want to trigger a re-assessment?',
      successMessage: 'Re-assessment initiated successfully',
      requiresSelection: true
    }
  ],

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fileAttachments from the main Risk Assessment data (handled separately)
    delete transformed.fileAttachments;

    // Audit: createdAt/createdById on create only; updatedAt/updatedById on update only (server / user-id header)
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    if (transformed.referenceDocument !== undefined) {
      if (transformed.referenceDocument === '' || transformed.referenceDocument === null) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(transformed.referenceDocument).trim();
      }
    }
    
    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = [
      'riskCategoryId', 'statusId'
    ];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert integer fields (impact, levelOfRisk, result)
    const integerFields = ['impact', 'levelOfRisk', 'result'];
    integerFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to proper Date objects
    const dateFields = ['requestedDate', 'receivedDate', 'closedAt'];
    
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          // Convert date string to Date object
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Custom handlers for Risk Assessment-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in riskAssessmentPage
    onDelete: null, // Will be defined in riskAssessmentPage
    onLoad: null, // Backend now provides fileAttachmentCount directly
    onFormOpen: null, // Will be defined in riskAssessmentPage
    
    // File upload handlers
    onFileUpload: null, // Will handle file upload after Risk Assessment save
    onFileRemove: null // Will handle file removal
  },

  // Validation configuration
  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true
  },

  // Messages
  messages: {
    create: {
      success: 'Risk assessment created successfully',
      error: 'Failed to create risk assessment'
    },
    update: {
      success: 'Risk assessment updated successfully', 
      error: 'Failed to update risk assessment'
    },
    delete: {
      success: 'Risk assessment deleted successfully',
      error: 'Failed to delete risk assessment',
      confirm: 'Are you sure you want to delete this risk assessment?'
    }
  },

  // Service Configuration
  service: {
    validateData: null, // Will be set by validation module
    validateField: null, // Will be set by validation module
    transformSubmissionData: function(data) {
      return this.transformData ? this.transformData(data) : data;
    }
  },

  // Dynamic table columns getter
  get tableColumns() {
    return this.fields
      .filter(field => field.table && field.table !== false)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
  },

  exportFileName: 'qms_risk_assessments',

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...riskAssessmentTrackingBase,
    fields: riskAssessmentTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: riskAssessmentTrackingLabels.createdDateLabel,
            userEmptyText: riskAssessmentTrackingLabels.notSpecifiedText,
            dateEmptyText: riskAssessmentTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: riskAssessmentTrackingLabels.updatedDateLabel,
              userEmptyText: riskAssessmentTrackingLabels.notModifiedText,
              dateEmptyText: riskAssessmentTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration
  labels: {
    singular: 'Risk Assessment',
    plural: 'Risk Assessments',
    createTitle: 'Create New Risk Assessment',
    editTitle: 'Edit Risk Assessment',
    viewTitle: 'View Risk Assessment',
    deleteTitle: 'Delete Risk Assessment'
  },

  // Help text for field descriptions
  helpText: {
    code: { short: 'Unique risk assessment identifier used for tracking and reference.' },
    title: { short: 'Brief, descriptive title of the risk being assessed.' },
    fileAttachments: { short: 'Supporting documents, evidence, or related files for this risk assessment.' },
    riskCategoryId: { short: 'Category or type of risk (operational, financial, strategic, compliance, etc.).' },
    statusId: { short: 'Current status of the risk assessment in the workflow.' },
    requestedDate: { short: 'Date when the risk assessment was requested or initiated.' },
    receivedDate: { short: 'Date when the risk assessment was received from the client.' },
    closedAt: { short: 'Date when the risk assessment was closed or completed.' },
    parameter: { short: 'Risk event/parameter description (e.g., "Άρνηση παραλαβής λόγω κατεστραμμένων τεμαχίων").' },
    effect: { short: 'Effect/consequence description (e.g., "Επιστροφή των τεμαχίων στην ΑΩ").' },
    impact: { short: 'Impact value on a 1-10 scale indicating the severity of the risk impact.' },
    rationale: { short: 'Explanation of the risk and its cause.' },
    levelOfRisk: { short: 'Level of risk on a 1-10 scale.' },
    result: { short: 'Result value (calculated or assessed).' },
    responsibleForActionType: { short: 'Type of entity responsible for taking action (User, Role, or Department).' },
    responsibleForActionUserIds: { short: 'The users responsible for taking action on this risk assessment (multiselect).' },
    responsibleForActionRoleIds: { short: 'The roles responsible for taking action on this risk assessment (multiselect).' },
    responsibleForActionDepartmentIds: { short: 'The departments responsible for taking action on this risk assessment (multiselect).' },
    involvedDepartmentsIds: { short: 'Departments involved in this risk assessment.' }
  },

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  shouldShowApprovalInActions: function() {
    return this.isApprovalEnabled() && this.approval.showInActions;
  },

  requiresApprovalForClose: function() {
    return this.isApprovalEnabled() && this.approval.requireApprovalForClose;
  }
};

export default riskAssessmentConfig;
